import BaseApi from '../base';

class ClinicApi extends BaseApi {
  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getNotes(token, user) {
    return this.makeRequest(this.METHOD_GET, `notes?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param {string} user
   * @param {string} text
   * @returns {Promise<*>}
   */
  async addNote(token, user, text) {
    return this.makeRequest(this.METHOD_POST, `notes`, token, { text, user });
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param {string} text
   * @returns {Promise<*>}
   */
  async updateNote(token, id, text) {
    return this.makeRequest(this.METHOD_POST, `notes/${id}`, token, { text });
  }

  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getConclusions(token, user) {
    return await this.makeRequest(this.METHOD_GET, `conclusions?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param data
   * @returns {Promise<*>}
   */
  async addConclusion(token, data) {
    return this.makeRequest(this.METHOD_POST, `conclusions`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param data
   * @returns {Promise<*>}
   */
  async updateConclusion(token, id, data) {
    return this.makeRequest(this.METHOD_POST, `conclusions/${id}`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @returns {Promise<*>}
   */
  async deleteConclusion(token, id) {
    return this.makeRequest(this.METHOD_DELETE, `conclusions/${id}`, token);
  }

  /**
   * @param {string} token
   * @returns {Promise<*>}
   */
  async getDiagnoses(token) {
    return await this.makeRequest(this.METHOD_GET, `diagnoses`, token);
  }

  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getDiagnosisEstablishments(token, user) {
    return await this.makeRequest(this.METHOD_GET, `diagnoses/establishment?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param data
   * @returns {Promise<*>}
   */
  async addDiagnosisEstablishment(token, data) {
    return this.makeRequest(this.METHOD_POST, `diagnoses/establishment`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param data
   * @returns {Promise<*>}
   */
  async updateDiagnosisEstablishment(token, id, data) {
    return this.makeRequest(this.METHOD_POST, `diagnoses/establishment/${id}`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @returns {Promise<*>}
   */
  async deleteDiagnosisEstablishment(token, id) {
    return this.makeRequest(this.METHOD_DELETE, `diagnoses/establishment/${id}`, token);
  }

  /**
   * @param {string} token
   * @returns {Promise<*>}
   */
  async getFunctionals(token) {
    return await this.makeRequest(this.METHOD_GET, `functionals`, token);
  }

  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getFunctionalEstablishments(token, user) {
    return await this.makeRequest(this.METHOD_GET, `functionals/establishment?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param data
   * @returns {Promise<*>}
   */
  async addFunctionalEstablishment(token, data) {
    return this.makeRequest(this.METHOD_POST, `functionals/establishment`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param data
   * @returns {Promise<*>}
   */
  async updateFunctionalEstablishment(token, id, data) {
    return this.makeRequest(this.METHOD_POST, `functionals/establishment/${id}`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @returns {Promise<*>}
   */
  async deleteFunctionalEstablishment(token, id) {
    return this.makeRequest(this.METHOD_DELETE, `functionals/establishment/${id}`, token);
  }

  /**
   * @param {string} token
   * @returns {Promise<*>}
   */
  async getHealthElements(token) {
    return this.makeRequest(this.METHOD_GET, `health`, token);
  }

  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getHealthState(token, user) {
    return this.makeRequest(this.METHOD_GET, `health/state?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param data
   * @returns {Promise<*>}
   */
  async addHealthState(token, data) {
    return this.makeRequest(this.METHOD_POST, `health/state`, token, data);
  }

  /**
   * @param {string} token
   * @returns {Promise<*>}
   */
  async getHealthContent(token) {
    return this.makeRequest(this.METHOD_GET, 'health/content', token);
  }

  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getAnamneses(token, user) {
    return await this.makeRequest(this.METHOD_GET, `anamnesis?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param data
   * @returns {Promise<*>}
   */
  async addAnamnesis(token, data) {
    return this.makeRequest(this.METHOD_POST, `anamnesis`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param data
   * @returns {Promise<*>}
   */
  async updateAnamnesis(token, id, data) {
    return this.makeRequest(this.METHOD_POST, `anamnesis/${id}`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @returns {Promise<*>}
   */
  async deleteAnamnesis(token, id) {
    return this.makeRequest(this.METHOD_DELETE, `anamnesis/${id}`, token);
  }

  /**
   * @param {string} token
   * @returns {Promise<*>}
   */
  async getStrategies(token) {
    return await this.makeRequest(this.METHOD_GET, `strategies`, token);
  }

  /**
   * @param {string} token
   * @param {string} user
   * @returns {Promise<*>}
   */
  async getStages(token, user) {
    return await this.makeRequest(this.METHOD_GET, `strategies/stages?user=${user}`, token);
  }

  /**
   * @param {string} token
   * @param data
   * @returns {Promise<*>}
   */
  async addStage(token, data) {
    return this.makeRequest(this.METHOD_POST, `strategies/stages`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param data
   * @returns {Promise<*>}
   */
  async updateStage(token, id, data) {
    return this.makeRequest(this.METHOD_POST, `strategies/stages/${id}`, token, data);
  }

  /**
   * @param {string} token
   * @param {string} id
   * @param {string} strategyStage
   * @param {string} status
   * @returns {Promise<*>}
   */
  async changeGoalStatus(token, id, strategyStage, status) {
    return this.makeRequest(this.METHOD_POST, `strategies/goals/${id}`, token, {status, strategyStage});
  }

  /**
   * @param {string} token
   * @param {number} id
   * @returns {Promise<*>}
   */
  async getDrugPrescriptions(token, id) {
    return this.makeRequest(this.METHOD_GET, `drug/prescriptions?user=${id}`, token);
  }
}

export default ClinicApi;
